import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/theme-toggle";
import { languageMeta, type AppCopy, type Language } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

interface NavigationMenuDemoProps {
  copy: AppCopy["nav"];
  language: Language;
  onLanguageChange: () => void;
}

export default function Component({
  copy,
  language,
  onLanguageChange,
}: NavigationMenuDemoProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isRTL = language === "he";
  const navigationLinks = copy.links;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* ── Header ── */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/95 px-4 text-foreground backdrop-blur-sm transition-colors md:px-6">
        <div className="mx-auto flex h-14 w-full max-w-7xl items-center justify-between gap-4">
          {/*
           * Burger is first in DOM → goes to "inline-start" side
           * RTL: inline-start = RIGHT   LTR: inline-start = LEFT
           */}
          <BurgerButton isOpen={isOpen} onClick={() => setIsOpen((v) => !v)} />

          {/* Controls on the opposite side */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageToggle
              language={language}
              onLanguageChange={onLanguageChange}
            />
          </div>
        </div>
      </header>

      {/* ── Backdrop ── */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={() => setIsOpen(false)}
        aria-hidden
      />

      {/* ── Drawer ── */}
      <aside
        dir={isRTL ? "rtl" : "ltr"}
        className={cn(
          "fixed top-0 z-50 flex h-full w-72 flex-col border-border/60 bg-background shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          isRTL ? "right-0 border-l" : "left-0 border-r",
          isRTL
            ? isOpen
              ? "translate-x-0"
              : "translate-x-full"
            : isOpen
              ? "translate-x-0"
              : "-translate-x-full",
        )}
      >
        {/* Drawer header */}
        <div className="flex h-14 items-center justify-between border-b border-border/60 px-5">
          <span className="text-sm font-medium tracking-widest text-foreground/50 uppercase">
            {isRTL ? "תפריט" : "Menu"}
          </span>
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
            className="flex h-8 w-8 items-center justify-center rounded-sm border border-border/60 text-foreground/60 transition-colors hover:bg-accent/40 hover:text-foreground"
          >
            <X size={14} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <ul className="space-y-0.5">
            {navigationLinks.map((link, index) => (
              <li key={index}>
                <a
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "flex items-center rounded-sm px-3 py-2.5 text-sm font-medium transition-colors",
                    index === 0
                      ? "bg-accent/60 text-foreground"
                      : "text-foreground/65 hover:bg-accent/40 hover:text-foreground",
                  )}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="my-5 h-px bg-border/50" />

          <ul className="space-y-0.5">
            <li>
              <a
                href="#cases"
                onClick={() => setIsOpen(false)}
                className="flex items-center rounded-sm px-3 py-2.5 text-sm font-medium text-foreground/65 transition-colors hover:bg-accent/40 hover:text-foreground"
              >
                {copy.caseStudies}
              </a>
            </li>
            <li className="mt-2">
              <a
                href="#contact"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center rounded-sm border border-border bg-transparent px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent/40"
              >
                {copy.bookCall}
              </a>
            </li>
          </ul>
        </nav>

        {/* Social links */}
        <div className="border-t border-border/60 px-5 py-5">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/40">
            {isRTL ? "רשתות חברתיות" : "Follow us"}
          </p>
          <div className="flex gap-2">
            {[
              { href: "#", label: "Facebook", svg: <FacebookIcon /> },
              { href: "#", label: "Instagram", svg: <InstagramIcon /> },
              { href: "#", label: "LinkedIn", svg: <LinkedInIcon /> },
            ].map(({ href, label, svg }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-sm border border-border/60 text-foreground/55 transition-colors hover:border-border hover:bg-accent/40 hover:text-foreground"
              >
                {svg}
              </a>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}

/* ── Burger button with animated lines → X ── */
function BurgerButton({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Toggle menu"
      aria-expanded={isOpen}
      className="relative flex h-9 w-9 items-center justify-center rounded-full bg-transparent text-foreground transition-colors hover:bg-accent/30"
    >
      <span className="flex h-4 w-[18px] flex-col items-end justify-between">
        <span
          className={cn(
            "h-0.5 rounded-full bg-current transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)]",
            isOpen ? "w-full translate-y-[7px] rotate-45" : "w-full",
          )}
        />
        <span
          className={cn(
            "h-0.5 w-3/4 rounded-full bg-current transition-all duration-200",
            isOpen ? "scale-x-0 opacity-0" : "scale-x-100 opacity-100",
          )}
        />
        <span
          className={cn(
            "h-0.5 rounded-full bg-current transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)]",
            isOpen ? "w-full -translate-y-[7px] -rotate-45" : "w-full",
          )}
        />
      </span>
    </button>
  );
}

/* ── Language toggle button ── */
function LanguageToggle({
  language,
  onLanguageChange,
}: {
  language: Language;
  onLanguageChange: () => void;
}) {
  const meta = languageMeta[language];

  return (
    <button
      type="button"
      aria-label={meta.ariaLabel}
      onClick={onLanguageChange}
      className="h-9 rounded-full bg-transparent px-2.5 text-xs font-medium tracking-[0.08em] text-foreground transition-colors hover:bg-accent/30"
    >
      <span>{meta.shortLabel}</span>
      <span className="mx-1 text-muted-foreground/60">/</span>
      <span>{meta.switchLabel}</span>
    </button>
  );
}

function FacebookIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}
